import { useCallback, useEffect, useMemo, useState } from 'react';
import { IComparisonFieldSetting } from '@models/winnerSelection/ITableDemand';

const STORAGE_PREFIX = 'winnerSelectionComparisonFields';

/**
 * Выбор дополнительных полей общий для всех позиций одной таблицы и хранится
 * локально у пользователя. Значения из старого конфига автоматически
 * отбрасываются, а отсутствие сохранённого выбора включает defaultVisible.
 */
export const useComparisonFields = (
    tableKey: string | undefined,
    fields: IComparisonFieldSetting[] | undefined,
    configuredMaxSelected: number | undefined,
) => {
    const availableFields = useMemo(() => fields ?? [], [fields]);
    const maxSelected = Math.max(1, Math.min(configuredMaxSelected ?? 2, availableFields.length || 1));
    const storageKey = `${STORAGE_PREFIX}:${tableKey ?? 'default'}`;
    const configSignature = availableFields.map((field) => `${field.key}:${field.defaultVisible}`).join('|');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useEffect(() => {
        const availableKeys = new Set(availableFields.map((field) => field.key));
        const defaults = availableFields
            .filter((field) => field.defaultVisible)
            .map((field) => field.key)
            .slice(0, maxSelected);

        try {
            const stored = window.localStorage.getItem(storageKey);
            if (stored !== null) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    setSelectedKeys(
                        availableFields
                            .filter((field) => parsed.includes(field.key) && availableKeys.has(field.key))
                            .map((field) => field.key)
                            .slice(0, maxSelected),
                    );
                    return;
                }
            }
        } catch {
            // Недоступный/повреждённый localStorage не должен ломать таблицу.
        }

        setSelectedKeys(defaults);
    }, [configSignature, storageKey, maxSelected, availableFields]);

    const updateSelectedKeys = useCallback(
        (keys: string[]) => {
            const requested = new Set(keys);
            const normalized = availableFields
                .filter((field) => requested.has(field.key))
                .map((field) => field.key)
                .slice(0, maxSelected);
            setSelectedKeys(normalized);
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(normalized));
            } catch {
                // В приватном режиме браузера персистенция может быть запрещена.
            }
        },
        [availableFields, maxSelected, storageKey],
    );

    const selectedFields = useMemo(() => {
        const selected = new Set(selectedKeys);
        return availableFields.filter((field) => selected.has(field.key));
    }, [availableFields, selectedKeys]);

    return { selectedKeys, selectedFields, maxSelected, updateSelectedKeys };
};
